import classNames from "classnames";
import { EagerLoaded } from "@common/types/base/orm";
import { Transaction } from "@common/types/transaction";
import { TransactionMatch } from "@common/types/transactionMatch";
import { addTimeZoneOffset, formatDateForTransaction } from "@helpers/date";
import { Amount } from "@design-system/Amount/Amount";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useDetachTransactionMatchMutation } from "@mutations/transaction";
import { DetachIcon } from "@icons";
import shellStyles from "@components/transactions/MatchedTransactionCard/MatchCardShell.module.scss";
import styles from "./TransactionMatches.module.scss";

interface Props {
    transactionMatch: EagerLoaded<TransactionMatch, "invoice">;
    financialTransaction: Transaction;
}

export const TransactionMatchInvoice: React.FC<Props> = ({
    financialTransaction,
    transactionMatch,
}) => {
    const { invoice } = transactionMatch;
    const detachMutation = useDetachTransactionMatchMutation(
        financialTransaction.id,
        transactionMatch.id,
    );

    return (
        <div className={styles.match} data-testid="transaction-invoice-match">
            <div className={classNames(shellStyles.shell, "mr-auto")}>
                <div
                    className={classNames(
                        shellStyles.control,
                        styles.invoiceMatchButton,
                    )}
                >
                    <span className={shellStyles.insideLabel}>
                        Matched invoice
                    </span>
                </div>
                <div className={styles.cardFooter}>
                    <div className={styles.cardFooterText}>
                        <span>
                            {formatDateForTransaction(
                                addTimeZoneOffset(new Date(invoice.createdAt)),
                            )}
                        </span>
                        <span className={styles.truncatedText}>
                            {invoice.counterparty?.name}
                        </span>
                    </div>
                    <div className={styles.cardFooterText}>
                        <span
                            className={classNames(
                                styles.cardFooterTextAmount,
                                styles.cardFooterTextAmountPositive,
                            )}
                        >
                            <Amount
                                amount={invoice.total}
                                separateCurrencySign
                            />
                        </span>
                    </div>
                </div>
            </div>

            <ButtonWithLoader
                data-testid="detach-invoice-match-button"
                variant="secondary"
                iconOnly
                size="sm"
                aria-label="Detach from this transaction"
                onClick={() => {
                    detachMutation.mutate();
                }}
                loading={detachMutation.isPending}
                tooltip="Detach from this transaction"
            >
                <DetachIcon />
            </ButtonWithLoader>
        </div>
    );
};
